// Centralized API client with automatic header management

export interface ApiResponse<T> {
  data: T;
  headers: Headers;
  status: number;
  statusText: string;
  ok: boolean;
}

export class ApiError extends Error {
  status: number;
  statusText: string;
  /** Parsed JSON error body (e.g. Rails validation errors), when present. */
  body?: unknown;

  constructor(status: number, statusText: string, body?: unknown) {
    super(statusText);
    this.name = "ApiError";
    this.status = status;
    this.statusText = statusText;
    this.body = body;
  }
}

interface ApiClientOptions extends RequestInit {
  token?: string;
  leagueAbbr?: string;
  skipLeagueHeader?: boolean;
  includeMetadata?: boolean;
}

// Global error handler for 401 responses (session timeout)
let unauthorizedHandler: (() => void) | null = null;

export function setUnauthorizedHandler(handler: () => void) {
  unauthorizedHandler = handler;
}

export function clearUnauthorizedHandler() {
  unauthorizedHandler = null;
}

export function handleUnauthorizedResponse(status: number) {
  if (status === 401 && unauthorizedHandler) {
    unauthorizedHandler();
  }
}

/**
 * Centralized API client that automatically includes:
 * - Content-Type: application/json
 * - Authorization header (if token provided)
 * - App-League header (if leagueAbbr provided)
 *
 * @param url - The URL to fetch
 * @param options - Request options including optional includeMetadata flag
 * @returns Promise of parsed JSON body, or ApiResponse with metadata if includeMetadata is true
 *
 * @example
 * // Standard usage (backward compatible)
 * const user = await apiGet<User>('/users/1', { token });
 *
 * @example
 * // With metadata
 * const response = await apiGet<User>('/users/1', { token, includeMetadata: true });
 * const authToken = response.headers.get('Authorization');
 * const user = response.data;
 */
// Overload 1: Default behavior (backward compatible)
export async function apiClient<T = any>(
  url: string,
  options?: ApiClientOptions & { includeMetadata?: false | undefined },
): Promise<T>;

// Overload 2: With metadata
export async function apiClient<T = any>(
  url: string,
  options: ApiClientOptions & { includeMetadata: true },
): Promise<ApiResponse<T>>;

// Implementation
export async function apiClient<T = any>(
  url: string,
  options: ApiClientOptions = {},
): Promise<T | ApiResponse<T>> {
  const {
    token,
    leagueAbbr,
    skipLeagueHeader,
    includeMetadata = false,
    ...fetchOptions
  } = options;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(fetchOptions.headers as Record<string, string>),
  };

  // Add Authorization header if token is provided
  if (token) {
    headers["Authorization"] = token;
  }

  // Add App-League header if leagueAbbr is provided and not explicitly skipped
  if (leagueAbbr && !skipLeagueHeader) {
    headers["App-League"] = leagueAbbr;
  }

  const response = await fetch(url, {
    ...fetchOptions,
    headers,
  });

  // Handle non-OK responses
  if (!response.ok) {
    // Handle 401 Unauthorized - session timeout
    if (response.status === 401 && unauthorizedHandler) {
      unauthorizedHandler();
    }

    const errorText = response.statusText || "Request failed";
    // Error bodies are optional and not always JSON; a missing or unparseable
    // body must not mask the HTTP error itself.
    const body = await response.json().catch(() => undefined);
    throw new ApiError(response.status, errorText, body);
  }

  // Parse JSON response (handle 204 No Content)
  let data;
  if (response.status === 204) {
    data = null;
  } else {
    data = await response.json();
  }

  // Return enhanced response if requested
  if (includeMetadata) {
    return {
      data,
      headers: response.headers,
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    };
  }

  // Default: backward compatible behavior
  return data;
}

/**
 * GET request wrapper
 */
// Overload 1: Default behavior
export function apiGet<T = any>(
  url: string,
  options?: ApiClientOptions & { includeMetadata?: false | undefined },
): Promise<T>;

// Overload 2: With metadata
export function apiGet<T = any>(
  url: string,
  options: ApiClientOptions & { includeMetadata: true },
): Promise<ApiResponse<T>>;

// Implementation
export function apiGet<T = any>(
  url: string,
  options: ApiClientOptions = {},
): Promise<T | ApiResponse<T>> {
  return apiClient<T>(url, { ...options, method: "GET" } as any);
}

/**
 * POST request wrapper
 */
// Overload 1: Default behavior
export function apiPost<T = any>(
  url: string,
  body: any,
  options?: ApiClientOptions & { includeMetadata?: false | undefined },
): Promise<T>;

// Overload 2: With metadata
export function apiPost<T = any>(
  url: string,
  body: any,
  options: ApiClientOptions & { includeMetadata: true },
): Promise<ApiResponse<T>>;

// Implementation
export function apiPost<T = any>(
  url: string,
  body: any,
  options: ApiClientOptions = {},
): Promise<T | ApiResponse<T>> {
  return apiClient<T>(url, {
    ...options,
    method: "POST",
    body: JSON.stringify(body),
  } as any);
}

/**
 * PUT request wrapper
 */
// Overload 1: Default behavior
export function apiPut<T = any>(
  url: string,
  body: any,
  options?: ApiClientOptions & { includeMetadata?: false | undefined },
): Promise<T>;

// Overload 2: With metadata
export function apiPut<T = any>(
  url: string,
  body: any,
  options: ApiClientOptions & { includeMetadata: true },
): Promise<ApiResponse<T>>;

// Implementation
export function apiPut<T = any>(
  url: string,
  body: any,
  options: ApiClientOptions = {},
): Promise<T | ApiResponse<T>> {
  return apiClient<T>(url, {
    ...options,
    method: "PUT",
    body: JSON.stringify(body),
  } as any);
}

/**
 * PATCH request wrapper
 */
// Overload 1: Default behavior
export function apiPatch<T = any>(
  url: string,
  body: any,
  options?: ApiClientOptions & { includeMetadata?: false | undefined },
): Promise<T>;

// Overload 2: With metadata
export function apiPatch<T = any>(
  url: string,
  body: any,
  options: ApiClientOptions & { includeMetadata: true },
): Promise<ApiResponse<T>>;

// Implementation
export function apiPatch<T = any>(
  url: string,
  body: any,
  options: ApiClientOptions = {},
): Promise<T | ApiResponse<T>> {
  return apiClient<T>(url, {
    ...options,
    method: "PATCH",
    body: JSON.stringify(body),
  } as any);
}

/**
 * DELETE request wrapper
 */
// Overload 1: Default behavior
export function apiDelete<T = any>(
  url: string,
  options?: ApiClientOptions & { includeMetadata?: false | undefined },
): Promise<T>;

// Overload 2: With metadata
export function apiDelete<T = any>(
  url: string,
  options: ApiClientOptions & { includeMetadata: true },
): Promise<ApiResponse<T>>;

// Implementation
export function apiDelete<T = any>(
  url: string,
  options: ApiClientOptions = {},
): Promise<T | ApiResponse<T>> {
  return apiClient<T>(url, { ...options, method: "DELETE" } as any);
}
